/**
 * Sports game type — shared TypeScript interfaces.
 *
 * All sports share the Family 2 mechanic: first-person camera behind the ball,
 * directional swipe to launch. The ISportDefinition strategy object encapsulates
 * everything that differs between sports (assets, physics, scoring, positions).
 */

// ─── Sport identity ────────────────────────────────────────────────────────────

export type SportType =
  | 'golf-driving'  // Phase 1: tee shot, distance scoring
  | 'football'      // Phase 2: field goal kick, binary 3-pt scoring
  | 'basketball'    // Phase 3: position-based 2/3-pt scoring
  | 'baseball'      // Phase 3: home run derby, distance scoring
  | 'soccer'        // Phase 3: penalty kick, zone scoring
  | 'ice-hockey';   // Phase 3: slap shot, zone scoring

// ─── Scoring model ─────────────────────────────────────────────────────────────

/**
 * How a sport scores each individual attempt.
 *
 * - 'distance-bands': Points from landing distance (golf-driving, baseball).
 *   The further the ball lands, the higher the band. Whiff = band 0.
 * - 'binary': Made or missed — flat points per make (football).
 *   Every successful kick is worth the same; difficulty comes from distance.
 * - 'position': Points defined per starting position (basketball 2-pt / 3-pt).
 *   BasketballSportDefinition.startingPositions[].pointsIfScored drives the value.
 * - 'zone': Hit-zone within goal determines points (soccer, ice-hockey).
 *   Top corners worth more than low center.
 */
export type ScoringModel = 'distance-bands' | 'binary' | 'position' | 'zone';

// ─── Prize determination ───────────────────────────────────────────────────────

export type PrizeDetermination =
  | 'flat'            // Everyone wins instantPrizeIndex regardless of score
  | 'score-threshold' // Prize tier from final score (skill-tested)
  | 'leaderboard'     // No instant prize; score logged for admin-deferred award
  | 'hybrid';         // Instant flat prize + score logged

export interface ScoreThreshold {
  minScore: number;
  prizeIndex: number;  // index into campaign.prizes[]
  label?: string;      // e.g. 'Bronze', 'Silver', 'Gold'
}

// ─── Distance bands (golf-driving, baseball) ──────────────────────────────────

/**
 * A scoring tier for distance-based sports.
 * Bands are sorted ascending by minDistance — the renderer picks the highest
 * band whose minDistance is ≤ the actual landing distance.
 */
export interface DistanceBand {
  /** Minimum landing distance (scene units) to qualify for this band */
  minDistance: number;
  points: number;
  /** Display label shown to the player. e.g. 'Good Drive', 'Home Run!' */
  label: string;
}

// ─── Game settings ─────────────────────────────────────────────────────────────

export interface SportsGameSettings {
  type: 'sports';
  sportType: SportType;

  /** Number of shot attempts per session (1 – maxAttempts, default 5) */
  attempts: number;

  /**
   * Assist factor [0–1].
   *   0 = pure skill (no trajectory correction)
   *   1 = ball always guided perfectly to target
   *
   * Whiff threshold formula: 1.0 - (assistFactor × 0.4)
   *   assistFactor 1.0 → whiff threshold 0.60 (very easy to avoid whiffing)
   *   assistFactor 0.5 → whiff threshold 0.80 (occasional hard-swing penalty)
   *   assistFactor 0.0 → whiff threshold 1.00 (impossible to whiff)
   */
  assistFactor: number;

  /**
   * Points per successful shot (fallback for binary / position scoring).
   * For distance-bands: used only if distanceBands is empty.
   * For position scoring: startingPositions[].pointsIfScored takes precedence.
   */
  pointsPerSuccess: number;

  /**
   * Distance bands for golf-driving and baseball.
   * Operator-configurable. Sorted ascending by minDistance.
   * If omitted, ISportDefinition.defaultDistanceBands is used.
   */
  distanceBands?: DistanceBand[];

  /**
   * Basketball only: when true, positions beyond the arc award 3 pts
   * (from BasketballSportDefinition.startingPositions[].pointsIfScored).
   * When false, all baskets use pointsPerSuccess.
   */
  enable3PointLine?: boolean;

  prizeDetermination: PrizeDetermination;
  instantPrizeIndex?: number;
  scoreThresholds?: ScoreThreshold[];

  /** Show running score HUD overlay during gameplay. Default true. */
  showScoreDisplay: boolean;

  // Internal flags (portal preview only)
  _previewPrizes?: boolean;
  _resetRequested?: boolean;
}

// ─── Shot input / result ───────────────────────────────────────────────────────

/**
 * Normalized shot input. Mode-agnostic — identical whether produced by AR or
 * non-AR touch input.
 *
 * Family 2 (first-person launch) mapping:
 *   direction.x = swipe horizontal component [-1 left … +1 right]; drives sidespin
 *   direction.y = launch elevation [0 = flat … 1 = near-vertical]
 *   power       = swipe velocity normalized [0.05…] — no upper cap; values above 1.0
 *                 represent a "hard" swing that may overshoot the target
 *   overSwing   = true when swipe speed exceeded the over-swing threshold;
 *                 sport definitions treat this as a whiff (mis-hit)
 */
export interface ShotInput {
  direction: { x: number; y: number };
  power: number;
  /** Player swung too fast — treated as a mis-hit by all sport definitions */
  overSwing?: boolean;
}

export interface ShotResult {
  shotIndex: number;
  input: ShotInput;
  scored: boolean;
  pointsEarned: number;
  /** Landing distance in scene units (distance-bands sports only) */
  distance?: number;
  /** Zone that was hit, or null for a miss (zone sports only) */
  zone?: string | null;
  isWhiff: boolean;
}

export interface SportsGameResult {
  totalScore: number;
  shots: ShotResult[];
  prizeIndex: number | null;
}

// ─── Game config (renderer input) ─────────────────────────────────────────────

export interface SportsGameConfig {
  settings: SportsGameSettings;
  prizes: { id: string; name: string; value: string }[];
  getPrizeIndex?: () => Promise<number>;
  _previewPrizes?: boolean;
  _resetRequested?: boolean;
}
